import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ETAPAS_FUNIL,
  ROTULO_CANAL,
  ROTULO_ETAPA,
  ROTULO_SCORE,
  formatarReais,
  type CanalContato,
  type EtapaFunil,
  type ResumoDoPainel,
  type Score,
  type StatusCadenciaAcao,
} from '@reducee/contracts';

import { chamarApi } from '../api/cliente.ts';
import type { ItemDaFila } from './FilaDeAtendimento.tsx';
import { Celula, Linha, Medalha, Pilula, Tabela, data } from './Superficie.tsx';

/**
 * As etapas que o funil mostra.
 *
 * "Perdido" fica de fora da faixa e do kanban, como no protótipo: quem olha o
 * funil quer ver onde há trabalho a fazer, e a perda já virou Reciclagem.
 */
export const ETAPAS_VISIVEIS = ETAPAS_FUNIL.filter((etapa) => etapa !== 'PERDIDO');

type AcaoDaCadencia = {
  id: string;
  ordem: number;
  canal: CanalContato;
  titulo: string;
  agendadaPara: string;
  status: StatusCadenciaAcao;
  executadaEm: string | null;
};

export type CadenciaEmAndamento = {
  id: string;
  status: string;
  iniciadaEm: string;
  totalDeAcoes: number;
  acoesExecutadas: number;
  oportunidade: {
    id: string;
    produto: string;
    score: Score;
    etapa: EtapaFunil;
    contato: { id: string; nome: string };
  };
  responsavel: { id: string; nome: string };
  proximaAcao: AcaoDaCadencia | null;
};

/**
 * Faixa de etapas do funil, com a contagem de cada uma.
 *
 * Fica acima de tudo no Sales porque é a leitura que se faz primeiro: onde as
 * Oportunidades estão paradas. A contagem vem pronta do servidor, sobre todas
 * as Oportunidades, e não da lista que a tela carrega aos poucos (NEX-227).
 */
export function FaixaDoFunil({
  porEtapa,
}: {
  porEtapa: ResumoDoPainel['porEtapa'] | undefined;
}): React.JSX.Element {
  return (
    <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
      {ETAPAS_VISIVEIS.map((etapa) => (
        <div
          key={etapa}
          data-teste={`funil-${etapa}`}
          className="min-w-0 rounded-[13px] border border-linha bg-superficie p-3"
        >
          <span className="text-[9px] font-extrabold uppercase text-texto-suave">
            {ROTULO_ETAPA[etapa]}
          </span>
          <b className="block text-xl">{porEtapa ? porEtapa[etapa] : '—'}</b>
        </div>
      ))}
    </div>
  );
}

/**
 * Os quatro sinais de que uma Oportunidade está encalhada.
 *
 * A fila traz o SLA e o tempo sem primeiro contato; o resumo do servidor traz
 * o próximo passo e o bloqueio do Contato, contados sobre todas.
 */
export function IndicadoresDeGestao({
  resumo,
}: {
  resumo: Pick<ResumoDoPainel, 'semProximoPasso' | 'bloqueados'> | undefined;
}): React.JSX.Element {
  const fila = useQuery({
    queryKey: ['atender-agora'],
    queryFn: () => chamarApi<ItemDaFila[]>('/atender-agora'),
  });

  const naFila = fila.data ?? [];
  const semPrimeiroContato = naFila.filter((item) => item.minutosSemPrimeiroContato > 0).length;
  const slaEstourado = naFila.filter((item) => item.estadoSla === 'ESTOURADO').length;

  const semProximoPasso = resumo?.semProximoPasso ?? '—';
  const bloqueados = resumo?.bloqueados ?? '—';

  const indicadores = [
    {
      teste: 'novos-sem-primeiro-contato',
      rotulo: 'Novos sem 1º contato',
      valor: semPrimeiroContato,
      detalhe: 'SLA padrão: 15 min',
    },
    { teste: 'sla-estourado', rotulo: 'SLA estourado', valor: slaEstourado },
    { teste: 'sem-proximo-passo', rotulo: 'Sem próximo passo', valor: semProximoPasso },
    { teste: 'bloqueados-para-contato', rotulo: 'Bloqueados para contato', valor: bloqueados },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {indicadores.map((indicador) => (
        <div
          key={indicador.teste}
          data-teste={indicador.teste}
          className="rounded-2xl border border-linha bg-superficie p-4.25 shadow-card"
        >
          <div className="text-[10px] font-extrabold uppercase tracking-wider text-texto-suave">
            {indicador.rotulo}
          </div>
          <div className="text-[28px] font-extrabold leading-tight text-marinho">
            {indicador.valor}
          </div>
          {indicador.detalhe ? (
            <div className="mt-1.25 text-[11px] text-texto-suave">{indicador.detalhe}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * Conversão por origem de aquisição.
 *
 * Mede quantas Oportunidades cada origem gerou, quantas chegaram a proposta ou
 * negociação e quantas fecharam — é o que diz onde vale investir. O agrupamento
 * é feito no servidor, sobre todas (`resumirPainelComercial`).
 */
export function ConversaoPorOrigem({
  linhas,
}: {
  linhas: ResumoDoPainel['conversao'] | undefined;
}): React.JSX.Element {
  if (!linhas) return <LinhaVazia>Carregando…</LinhaVazia>;
  if (linhas.length === 0) return <LinhaVazia>Sem dados.</LinhaVazia>;

  return (
    <div>
      {linhas.map((linha) => (
        <div
          key={linha.rotulo}
          data-teste="conversao-origem"
          className="flex items-center justify-between gap-3 border-b border-linha py-2.75 last:border-0"
        >
          <div className="min-w-0">
            <b className="block truncate">{linha.rotulo}</b>
            <div className="truncate text-xs text-texto-suave">
              {linha.leads} leads · {linha.propostas} proposta/negociação · {linha.fechadas} fechados
            </div>
          </div>
          <div className="shrink-0 text-right">
            <b>{linha.leads ? Math.round((linha.fechadas / linha.leads) * 100) : 0}%</b>
            <div className="text-xs text-texto-suave">{formatarReais(linha.valorCentavos)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Estado vazio no formato do protótipo: discreto e sem caixa própria. */
export function LinhaVazia({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="py-4.5 text-xs text-texto-suave">{children}</div>;
}

/** Carrega as cadências ativas no alcance da usuária. */
function useCadenciasEmAndamento(): CadenciaEmAndamento[] {
  const consulta = useQuery({
    queryKey: ['cadencias'],
    queryFn: () => chamarApi<CadenciaEmAndamento[]>('/cadencias'),
  });
  return consulta.data ?? [];
}

/**
 * As tentativas de contato marcadas para hoje ou já vencidas.
 *
 * Uma ação de ontem que ninguém executou continua pendente e aparece aqui: o
 * painel é a lista do que fazer, não um calendário do dia.
 */
export function ContatosDeHoje(): React.JSX.Element {
  const cadencias = useCadenciasEmAndamento();

  const fimDeHoje = new Date();
  fimDeHoje.setHours(23, 59, 59, 999);

  const vencendo = cadencias
    .filter((cadencia) => cadencia.proximaAcao !== null)
    .filter((cadencia) => new Date(cadencia.proximaAcao!.agendadaPara) <= fimDeHoje)
    .slice(0, 6);

  if (vencendo.length === 0) return <LinhaVazia>Nenhum contato vencendo hoje.</LinhaVazia>;

  return (
    <div>
      {vencendo.map((cadencia) => (
        <div
          key={cadencia.id}
          data-teste="contato-de-hoje"
          className="flex items-center justify-between gap-3 border-b border-linha py-2.75 last:border-0"
        >
          <div className="min-w-0">
            <b className="block truncate">{cadencia.oportunidade.contato.nome}</b>
            <div className="truncate text-xs text-texto-suave">
              {ROTULO_CANAL[cadencia.proximaAcao!.canal]} · {cadencia.proximaAcao!.titulo} ·{' '}
              {data(cadencia.proximaAcao!.agendadaPara)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Histórico e próxima ação por lead, como a tabela do protótipo. */
export function TabelaDeCadencias(): React.JSX.Element {
  const cadencias = useCadenciasEmAndamento();
  const clienteDeConsultas = useQueryClient();

  async function recarregar(): Promise<void> {
    await clienteDeConsultas.invalidateQueries({ queryKey: ['cadencias'] });
    await clienteDeConsultas.invalidateQueries({ queryKey: ['atender-agora'] });
  }

  // Executar registra a tentativa e anda o roteiro; pausar interrompe sem
  // apagar o que já saiu, para quem respondeu não continuar recebendo.
  const executar = useMutation({
    mutationFn: ({ cadenciaId, acaoId }: { cadenciaId: string; acaoId: string }) =>
      chamarApi(`/cadencias/${cadenciaId}/acoes/${acaoId}/executar`, { metodo: 'POST', corpo: {} }),
    onSuccess: recarregar,
  });

  const pausar = useMutation({
    mutationFn: (cadenciaId: string) =>
      chamarApi(`/cadencias/${cadenciaId}`, { metodo: 'PATCH', corpo: { status: 'PAUSADA' } }),
    onSuccess: recarregar,
  });

  return (
    <Tabela
      colunas={[
        'Lead',
        'Score',
        'Responsável',
        'Início',
        'Etapa atual',
        'Próxima ação',
        'Data',
        'Status',
        'Ações',
      ]}
      vazio="Nenhuma cadência ativa."
    >
      {cadencias.map((cadencia) => (
        <Linha key={cadencia.id}>
          <Celula>
            <strong>{cadencia.oportunidade.contato.nome}</strong>
            <div className="text-xs text-texto-suave">
              {ROTULO_ETAPA[cadencia.oportunidade.etapa]}
            </div>
          </Celula>
          <Celula>
            <Medalha score={cadencia.oportunidade.score}>
              {ROTULO_SCORE[cadencia.oportunidade.score]}
            </Medalha>
          </Celula>
          <Celula>{cadencia.responsavel.nome}</Celula>
          <Celula>{data(cadencia.iniciadaEm)}</Celula>
          <Celula>
            {cadencia.acoesExecutadas}/{cadencia.totalDeAcoes}
          </Celula>
          <Celula>
            {cadencia.proximaAcao
              ? `${ROTULO_CANAL[cadencia.proximaAcao.canal]} · ${cadencia.proximaAcao.titulo}`
              : '—'}
          </Celula>
          <Celula>{data(cadencia.proximaAcao?.agendadaPara)}</Celula>
          <Celula>
            <Pilula tom="informacao">Ativa</Pilula>
          </Celula>
          <Celula>
            <div className="flex gap-1.5">
              {cadencia.proximaAcao ? (
                <button
                  type="button"
                  onClick={() =>
                    executar.mutate({
                      cadenciaId: cadencia.id,
                      acaoId: cadencia.proximaAcao!.id,
                    })
                  }
                  className="rounded-lg border border-linha px-2 py-1.5 text-[10px] font-bold transition hover:bg-marinho-suave"
                >
                  Executar
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => pausar.mutate(cadencia.id)}
                className="rounded-lg border border-linha px-2 py-1.5 text-[10px] font-bold transition hover:bg-marinho-suave"
              >
                Registrar resposta
              </button>
            </div>
          </Celula>
        </Linha>
      ))}
    </Tabela>
  );
}
